import { Button, CloseIcon, MenuIcon } from "@eworkit/iris";
import { isFunction } from "lodash-es";
import React, { FC, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { FullScreenDropdown, MenuItem } from "@/components/Navigation";
import { MEDIA_SIZE_MD } from "@/core/media";
import { Flex } from "@/eworkitComponents";
import { useOnLanguageChange } from "@/hooks/Translations";
import { RouteButton } from "@/infrastructure/Route";
import { TNavItemVariant } from "@/views/components/Layout/components/Topbar/components/NavItem";
import { RouteNavItem } from "@/views/components/Layout/components/Topbar/components/RouteNavItem";
import { router } from "@/views/router";

import "./_navigation.scss";

const createAccountRoute = router.get("register-form");

interface INavigationProps {
  onOpen?: () => void;
  onClose?: () => void;
}

export const PublicNavigation: FC<INavigationProps> = React.memo(() => {
  const { t } = useTranslation(["public"]);

  const lang = useOnLanguageChange();

  const isMediumScreen = useMedia(MEDIA_SIZE_MD);

  const fld: any = useRef(null);

  const baseClass = "navigation";

  const jobRequestsRoute = router.get("public-job-requests");

  const handleClose = useCallback(() => {
    const closeButton: any = document.querySelector(`[aria-controls="${fld?.current?.id}"]`);
    if (closeButton && isFunction(closeButton.click)) {
      closeButton.click();
    }
  }, []);

  const DropdownTarget = ({ onClick, ...restDropdownTargetProps }: any) => {
    const onClickHandler = useCallback(e => {
      const contextDropdownOpen = document.getElementById("context-dropdown-close");
      if (contextDropdownOpen) {
        contextDropdownOpen.click();
      }
      onClick(e);
    }, []);
    return <Button {...restDropdownTargetProps} onClick={onClickHandler} />;
  };

  const renderMobile = () => {
    return (
      <FullScreenDropdown
        ref={fld}
        as={DropdownTarget}
        size="xxl"
        hideOnClickOutside={true}
        openDisclosureProps={{ iconLeft: <CloseIcon id="navigation-close-menu-icon" />, variant: "ghost" }}
        closedDisclosureProps={{
          iconLeft: (
            <div>
              <MenuIcon id="navigation-menu-icon" />
            </div>
          ),
          variant: "ghost",
        }}>
        <Flex direction="column" background="secondary">
          <Flex direction="column" padding={["md", "md", "xs", "md"]}>
            {renderMenuItems("mobile", true)}

            <RouteButton className={`${baseClass}__button`} path={createAccountRoute.path} variant="primary" size="lg">
              {t("LANDING_PAGE.BUTTON_CREATE_ACCOUNT")}
            </RouteButton>
          </Flex>
        </Flex>
      </FullScreenDropdown>
    );
  };

  const renderDesktop = () => {
    return (
      <Flex className={baseClass} direction="row" align="center" justify="flex-start" innerMargin="md">
        {renderMenuItems("text")}
      </Flex>
    );
  };

  const renderMenuItems = (variant: TNavItemVariant, mobile?: boolean) => {
    return (
      <>
        <MenuItem
          as={RouteNavItem}
          path={`/${lang}${jobRequestsRoute.path}`}
          exact={false}
          variant={variant}
          text={t("TOPBAR.JOBS")}
          onClick={mobile ? handleClose : undefined}
        />

        <MenuItem
          as={RouteNavItem}
          exact={false}
          variant={variant}
          text={t("TOPBAR.EWORK+")}
          onClick={() => window.open("https://www.eworkgroup.com/find-work/discounts-and-benefits", "_blank")}
        />
      </>
    );
  };

  return isMediumScreen ? renderMobile() : renderDesktop();
});
